// Used in PDP, Product Card

import { AiOutlineCheckCircle, AiOutlineClose } from "react-icons/ai";
import { useRouter } from "next/router";
import { ADD_PRODUCT_FEES, DEL_PRODUCT_FEES } from "@/services/cart";
import ImgTag from "../Helpers/ImgTag";
import ProductsBy from "../Helpers/ProductsBy";
import { cardInfoBreakPoints } from "@/constants/BreakPoints";
import BrowserStorage from "store";
import { useTranslation } from "next-i18next";
import { formatPriceFixed } from "@/utils/global/handlePrice";

const getDiscountPercent = (prices) => {
  const originalPrice = Number(prices?.original_price);
  const price = Number(prices?.price);
  if (!(originalPrice > 0) || !Number.isFinite(price)) return 0;
  return parseInt(((originalPrice - price) / originalPrice) * 100);
};

// A product without a markdown shows no badge rather than "0.00%".
function DiscountBadge({ prices, t }) {
  const discountPercent = getDiscountPercent(prices);
  if (discountPercent <= 0) return null;

  return (
    <span className="px-3 py-1 text-sm font-bold text-white rounded-lg bg-darkgold">
      {t("Discount")} {discountPercent.toFixed(2)}%
    </span>
  );
}

function CartInfo({ products, active, setActive, relatedProducts, totals }) {
  const { t } = useTranslation("common");
  const bannerImg = BrowserStorage.get("popup-banner");
  const { push } = useRouter();

  const handleAddProdFees = async (cart_id, data) => {
    try {
      await ADD_PRODUCT_FEES(cart_id, data);
    } catch (error) {}
  };

  const handleDelProdFees = async (cart_id, data) => {
    try {
      await DEL_PRODUCT_FEES(cart_id, data);
    } catch (error) {}
  };

  return active ? (
    <div className="fixed top-0 left-0 w-screen bg-lightblack z-[9999]  h-screen">
      <div className="w-[570px] max-[570px]:w-full bg-white absolute top-0 left-0 h-screen">
        <div className="sticky top-0 z-20 flex items-center justify-between p-4 text-white bg-red">
          <div className="flex items-center">
            <AiOutlineCheckCircle size="20px" />
            <h4 className="mx-2 text-lg font-semibold">{t("GreatChoice")}</h4>
          </div>
          <AiOutlineClose
            onClick={() => setActive(false)}
            size="20px"
            className="cursor-pointer"
          />
        </div>
        <div className="h-[80%] overflow-y-scroll red-scrollbar">
          <div className="px-2">
            {products?.length
              ? products?.map((product) => (
                  <>
                    <div
                      key={product?.sku}
                      className="flex items-center py-4 border-b"
                    >
                      <ImgTag
                        src={`${
                          process.env.NEXT_PUBLIC_IMAGE_BASEURL
                        }/media/catalog/product${product?.image || ""}`}
                        className="object-contain w-auto h-24 p-2 border rounded-md"
                        height={400}
                        width={600}
                        alt="Prod Cart Img!"
                      />
                      <div className="mx-2">
                        <p className="mb-1 font-semibold text-zinc-600">
                          {product?.name || ""}
                        </p>
                      </div>
                    </div>
                    <div className="py-4 border-b">
                      <div className="flex items-center justify-between mb-2 max-[420px]:block">
                        <div className="flex items-center max-[420px]:justify-center">
                          <h4 className="font-bold">
                            {formatPriceFixed(
                              product?.prices_with_tax?.original_price
                            )}{" "}
                            {t("SAR")}
                          </h4>
                          <span className="mx-2">
                            ({t("IncludesValueAddedTax")})
                          </span>
                        </div>
                        {product?.back_orders === "101" ? (
                          <span className="text-lg font-semibold text-red max-[420px]:text-center max-[420px]:block">
                            {t("PreOrder")}
                          </span>
                        ) : null}
                      </div>
                      <div className="flex items-center max-[420px]:justify-center">
                        {Number(product?.prices_with_tax?.price) ===
                          Number(product?.prices_with_tax?.original_price) ||
                        product?.prices_with_tax?.discounted_price_to ===
                          "" ? null : (
                          <h4 className="font-bold line-through text-zinc-500">
                            {formatPriceFixed(
                              product?.prices_with_tax?.original_price
                            )}{" "}
                            {t("SAR")}
                          </h4>
                        )}

                        <h4 className="mx-3 font-bold text-red">
                          {formatPriceFixed(product?.prices_with_tax?.price)}{" "}
                          {t("SAR")}
                        </h4>
                        <DiscountBadge
                          prices={product?.prices_with_tax}
                          t={t}
                        />
                      </div>
                    </div>
                    <div>
                      {product?.product_fees?.length
                        ? product?.product_fees.map((fee, i) => (
                            <div className="py-4" key={i}>
                              <h4 className="mb-2 text-lg font-semibold text-red">
                                {fee.title}
                              </h4>
                              <p className="mb-2 text-zinc-500">
                                {fee.description}
                              </p>
                              <div className="grid grid-cols-3 gap-3 max-[420px]:grid-cols-2">
                                {Object.values(fee?.options || {}).map(
                                  (option, idx) => (
                                    <div
                                      key={idx}
                                      onClick={() => {
                                        if (active === false) {
                                          handleAddProdFees(product?.quote_id, {
                                            feeData: {
                                              id: option.fee_id,
                                              options: option.fee_option_id,
                                              itemId: product?.item_id,
                                            },
                                          });
                                        } else {
                                          handleDelProdFees(product?.quote_id, {
                                            feeData: {
                                              id: option.fee_id,
                                              itemId: product?.item_id,
                                            },
                                          });
                                        }
                                        setActive(active === idx ? false : idx);
                                      }}
                                      className="cursor-pointer border p-1.5 rounded-md relative"
                                    >
                                      <h3 className="mb-1 font-semibold">
                                        {option.title}
                                      </h3>
                                      <h5 className="font-bold">
                                        {parseInt(option.price || 0).toFixed(1)}{" "}
                                        {t("SAR")}
                                      </h5>
                                      {active === idx && (
                                        <AiOutlineCheckCircle
                                          size="24px"
                                          className="absolute text-emerald-700 top-2 right-2"
                                        />
                                      )}
                                    </div>
                                  )
                                )}
                              </div>
                            </div>
                          ))
                        : null}
                    </div>
                  </>
                ))
              : null}

            {bannerImg?.url ? (
              <ImgTag
                src={bannerImg?.url}
                className="object-contain w-full h-auto"
                height={200}
                width={400}
                alt="Tamara Sheet Img!"
              />
            ) : null}
            <div className="z-0">
              <ProductsBy
                noOutOfStockProducts={true}
                width="w-full"
                breakpoints={cardInfoBreakPoints}
                show={false}
                title="Related Products"
                products={relatedProducts || []}
              />
            </div>
          </div>
        </div>
        <div className="sticky bottom-0 p-4 bg-white border-t">
          {totals != null ? (
            <div className="flex items-center justify-between mx-2 mb-2">
              <h3 className="text-xl font-bold">{t("Total")}</h3>
              <h4 className="text-xl font-bold text-red">
                {totals} {t("SAR")}
              </h4>
            </div>
          ) : null}
          <div className="grid grid-cols-2 gap-2 bg-white">
            <button
              onClick={() => push("/cart")}
              className="w-full p-2 font-semibold text-white border rounded-full bg-blue border-red campaign:bg-campaignAccent"
            >
              {t("GoToCartPage")}
            </button>
            <button
              onClick={() => push("/")}
              className="w-full p-2 font-semibold border rounded-full text-red border-red"
            >
              {t("ContinueShopping")}
            </button>
          </div>
        </div>
      </div>
    </div>
  ) : null;
}

export default CartInfo;
